import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useFormTracking } from '../../hooks/useAnalytics';

const UseCaseStep = ({ data, updateData, onNext, onBack }) => {
  const { 
    trackStepStart, 
    trackStepCompleted, 
    trackFieldInteraction,
    trackValidationFailure,
    trackSpecialFieldSelection 
  } = useOnboarding();
  
  const { 
    trackFormStart,
    trackFieldFocus, 
    trackFieldChange,
    trackFormSubmit 
  } = useFormTracking('onboarding_step_3_usecase');
  
  const [formData, setFormData] = useState({
    primaryUseCase: data.primaryUseCase || '',
    primaryUseCaseOther: data.primaryUseCaseOther || '',
    mainGoals: data.mainGoals || [],
    expectedVolume: data.expectedVolume || ''
  });
  const [errors, setErrors] = useState({});
  const [showOtherUseCase, setShowOtherUseCase] = useState(false);
  const fieldRefs = useRef({});
  const stepStartTime = useRef(Date.now());
  const goalInteractions = useRef({});

  const useCases = [
    'Customer Support Automation',
    'Lead Generation & Qualification',
    'Internal HR Assistant',
    'Sales Process Automation',
    'Content Creation & Marketing',
    'Data Analysis & Reporting',
    'Appointment Scheduling',
    'E-commerce Product Recommendations',
    'Training & Education',
    'Other'
  ];

  const mainGoals = [
    'Reduce Response Times',
    'Increase Customer Satisfaction',
    'Generate More Leads',
    'Automate Repetitive Tasks',
    'Improve Team Productivity',
    'Scale Customer Support',
    'Enhance User Experience',
    'Reduce Operational Costs'
  ];

  const volumeOptions = [
    { value: 'low', label: 'Low (< 100 conversations/month)' },
    { value: 'medium', label: 'Medium (100-1,000 conversations/month)' },
    { value: 'high', label: 'High (1,000-10,000 conversations/month)' },
    { value: 'very-high', label: 'Very High (10,000+ conversations/month)' }
  ];

  // Track step start on mount
  useEffect(() => {
    trackStepStart(3, 'ai_requirements');
    trackFormStart();
    
    // Check if use case is "Other" and show input
    if (data.primaryUseCase === 'Other') {
      setShowOtherUseCase(true);
    }
  }, [trackStepStart, trackFormStart, data.primaryUseCase]);

  const handleUseCaseChange = (useCase) => {
    const newFormData = { ...formData, primaryUseCase: useCase };
    setFormData(newFormData);
    updateData(newFormData);
    
    // Track field interaction
    trackFieldInteraction(3, 'primaryUseCase', 'change', useCase);
    trackFieldChange('primaryUseCase', useCase);
    
    // Clear error when user makes selection
    if (errors.primaryUseCase) {
      setErrors(prev => ({ ...prev, primaryUseCase: '' }));
    }
    
    // Handle special "Other" selection
    const isOther = useCase === 'Other';
    setShowOtherUseCase(isOther);
    
    if (!isOther) {
      // Clear other field if not selected
      const updatedData = { ...newFormData, primaryUseCaseOther: '' };
      setFormData(updatedData);
      updateData(updatedData);
    }
    
    // Track special selection
    trackSpecialFieldSelection('primaryUseCase', useCase, isOther);
  };

  const handleGoalChange = (goal, checked) => {
    let newGoals;
    if (checked) {
      newGoals = [...formData.mainGoals, goal];
    } else {
      newGoals = formData.mainGoals.filter(item => item !== goal);
    }
    
    const newFormData = { ...formData, mainGoals: newGoals };
    setFormData(newFormData);
    updateData(newFormData);
    
    // Track goal interaction
    if (!goalInteractions.current[goal]) {
      goalInteractions.current[goal] = 0;
    }
    goalInteractions.current[goal]++;
    
    trackFieldInteraction(3, 'mainGoals', checked ? 'selected' : 'deselected', {
      goal: goal,
      total_selected: newGoals.length,
      interaction_count: goalInteractions.current[goal]
    });
    
    // Track as main goals selection event
    trackFieldInteraction(3, 'main_goals_selected', 'change', {
      selected_goals: newGoals,
      total_goals: newGoals.length,
      action: checked ? 'added' : 'removed',
      goal: goal
    });
    
    // Clear error when user makes selection
    if (errors.mainGoals) {
      setErrors(prev => ({ ...prev, mainGoals: '' }));
    }
  };

  const handleInputChange = (field, value) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);
    updateData(newFormData);
    
    // Track field interaction
    trackFieldInteraction(3, field, 'change', value);
    trackFieldChange(field, value);
    
    // Track specific events
    if (field === 'expectedVolume') {
      trackFieldInteraction(3, 'expected_volume_selected', 'change', value);
    }
    
    // Clear error when user starts typing/selecting
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  /**
   * Handle field focus with analytics
   */
  const handleFieldFocus = (field) => {
    trackFieldInteraction(3, field, 'focus');
    trackFieldFocus(field);
  };
  
  /**
   * Handle field blur with analytics
   */
  const handleFieldBlur = (field, value) => {
    trackFieldInteraction(3, field, 'blur', value);
  };

  const validateForm = () => {
    const newErrors = {};
    let validationFailures = [];
    
    // Validate primary use case
    if (!formData.primaryUseCase) {
      newErrors.primaryUseCase = 'Please select a primary use case';
      validationFailures.push('primaryUseCase');
    }
    
    // Validate "Other" use case input if selected
    if (formData.primaryUseCase === 'Other' && !formData.primaryUseCaseOther.trim()) {
      newErrors.primaryUseCaseOther = 'Please specify your use case';
      validationFailures.push('primaryUseCaseOther');
    }
    
    // Validate main goals
    if (formData.mainGoals.length === 0) {
      newErrors.mainGoals = 'Please select at least one main goal';
      validationFailures.push('mainGoals');
    }
    
    // Validate expected volume
    if (!formData.expectedVolume) {
      newErrors.expectedVolume = 'Please select your expected volume';
      validationFailures.push('expectedVolume');
    }
    
    // Track validation failures
    validationFailures.forEach(field => {
      trackValidationFailure(3, field, newErrors[field]);
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateForm()) {
      const timeSpent = Math.round((Date.now() - stepStartTime.current) / 1000);
      
      // Track step completion
      trackStepCompleted(3, 'ai_requirements');
      
      // Track form submission
      trackFormSubmit(true);
      
      // Track collected data with detailed analytics
      const fieldsCompleted = [
        formData.primaryUseCase ? 'primaryUseCase' : null,
        formData.primaryUseCaseOther ? 'primaryUseCaseOther' : null,
        formData.mainGoals.length > 0 ? 'mainGoals' : null,
        formData.expectedVolume ? 'expectedVolume' : null
      ].filter(Boolean);
      
      trackFieldInteraction(3, 'step_completed', 'submit', {
        fields_completed: fieldsCompleted,
        time_spent: timeSpent,
        goals_selected: formData.mainGoals.length,
        use_case_is_other: formData.primaryUseCase === 'Other',
        expected_volume_category: formData.expectedVolume
      });
      
      onNext();
    } else {
      // Track form submission failure
      trackFormSubmit(false, Object.keys(errors));
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="text-center space-y-2">
        <h2 className="text-xl font-bold text-card-foreground">
          What are your goals with AI agents?
        </h2>
        <p className="text-sm text-muted-foreground">
          Help us understand your primary use cases and objectives.
        </p>
      </div>

      <div className="flex flex-col gap-8">
        {/* Primary Use Case */}
        <div className="space-y-4">
          <div>
            <Label className="text-base font-semibold text-card-foreground">
              Primary Use Case for AI Agents *
            </Label>
            <p className="text-sm text-muted-foreground mt-1">Select your main use case</p>
          </div>
          
          <Select
            value={formData.primaryUseCase}
            onValueChange={handleUseCaseChange}
            onOpenChange={(open) => {
              if (open) handleFieldFocus('primaryUseCase');
              else handleFieldBlur('primaryUseCase', formData.primaryUseCase);
            }}
          >
            <SelectTrigger 
              id="primaryUseCase"
              className={errors.primaryUseCase ? 'border-red-500' : ''}
            >
              <SelectValue placeholder="Select your primary use case" />
            </SelectTrigger>
            <SelectContent>
              {useCases.map((useCase) => (
                <SelectItem key={useCase} value={useCase}>
                  {useCase}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {errors.primaryUseCase && (
            <p className="text-sm text-red-600">{errors.primaryUseCase}</p>
          )}

          {/* Other Use Case Text Field */}
          {showOtherUseCase && (
            <div className="mt-4 space-y-2">
              <Label htmlFor="primaryUseCaseOther" className="text-card-foreground">
                Please specify your use case *
              </Label>
              <Input
                id="primaryUseCaseOther"
                ref={el => fieldRefs.current.primaryUseCaseOther = el}
                type="text"
                placeholder="Describe your specific use case..."
                value={formData.primaryUseCaseOther}
                onChange={(e) => handleInputChange('primaryUseCaseOther', e.target.value)}
                onFocus={() => handleFieldFocus('primaryUseCaseOther')}
                onBlur={(e) => handleFieldBlur('primaryUseCaseOther', e.target.value)}
                className={errors.primaryUseCaseOther ? 'border-red-500' : ''}
              />
              {errors.primaryUseCaseOther && (
                <p className="text-sm text-red-600">{errors.primaryUseCaseOther}</p>
              )}
            </div>
          )}
        </div>

        {/* Main Goals */}
        <div className="space-y-4">
          <div>
            <Label className="text-base font-semibold text-card-foreground">
              Main Goals *
            </Label>
            <p className="text-sm text-muted-foreground mt-1">What do you hope to achieve?</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {mainGoals.map((goal) => (
              <div key={goal} className={`relative cursor-pointer transition-all duration-200 ${
                formData.mainGoals.includes(goal) 
                  ? 'bg-blue-50 border-blue-500 ring-2 ring-blue-200' 
                  : 'bg-card border-border hover:border-border hover:bg-muted'
              } rounded-xl border-2 p-4`}>
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id={goal}
                    checked={formData.mainGoals.includes(goal)}
                    onCheckedChange={(checked) => handleGoalChange(goal, checked)}
                    className="border-2 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                  />
                  <Label htmlFor={goal} className="flex-1 cursor-pointer font-medium text-card-foreground">
                    {goal}
                  </Label>
                </div>
                {formData.mainGoals.includes(goal) && (
                  <div className="absolute top-3 right-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {errors.mainGoals && (
            <p className="text-sm text-red-600">{errors.mainGoals}</p>
          )}
        </div>

        {/* Expected Volume */}
        <div className="space-y-4">
          <Label className="text-base font-semibold text-card-foreground">
            Expected Volume *
          </Label>
          <Select
            value={formData.expectedVolume}
            onValueChange={(value) => handleInputChange('expectedVolume', value)}
            onOpenChange={(open) => {
              if (open) handleFieldFocus('expectedVolume');
              else handleFieldBlur('expectedVolume', formData.expectedVolume);
            }}
          >
            <SelectTrigger 
              id="expectedVolume"
              className={errors.expectedVolume ? 'border-red-500' : ''}
            >
              <SelectValue placeholder="Select your expected conversation volume" />
            </SelectTrigger>
            <SelectContent>
              {volumeOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.expectedVolume && (
            <p className="text-sm text-red-600">{errors.expectedVolume}</p>
          )}
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between items-center pt-6">
        <Button
          onClick={onBack}
          variant="outline"
          className="px-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        
        <Button
          onClick={handleNext}
          className="px-6"
        >
          Continue
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default UseCaseStep;